import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Drawer from '../Common/Drawer';
import ConfirmModal from '../Common/ConfirmModal';
import { useBatchCart, type BatchCartItem } from '../../contexts/BatchCartContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useUser } from '../../contexts/UserContext';

const BatchCartDrawer: React.FC = () => {
  const {
    cart,
    removeItem,
    clearCart,
    itemCount,
    isOpen,
    setIsOpen,
    submitBatch,
    submitting,
    submitResult,
    clearResult,
  } = useBatchCart();
  const { canCreateBatchReview, isStaff } = useEntitlements();
  const { activeTeam, teamMemberships } = useUser();
  const planId = activeTeam?.plan_id || teamMemberships[0]?.plan_id;
  const showUpgrade = !isStaff && planId !== 'institutional';
  const [showConfirm, setShowConfirm] = useState(false);
  const [dryRunMode, setDryRunMode] = useState(false);

  // Group items by review type for display
  const groupedItems = cart.items.reduce<Record<string, BatchCartItem[]>>((acc, item) => {
    if (!acc[item.reviewType]) {
      acc[item.reviewType] = [];
    }
    acc[item.reviewType].push(item);
    return acc;
  }, {});

  const handleSubmit = async () => {
    setShowConfirm(false);
    await submitBatch(dryRunMode);
  };

  const handleSubmitClick = (dryRun: boolean) => {
    setDryRunMode(dryRun);
    setShowConfirm(true);
  };

  return (
    <>
      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} title={`Batch Review Queue (${itemCount})`} subtitle="Issuers staged for batch LLM review" width="lg">
        <div className="flex flex-col h-full">
          {/* Provider badge + clear */}
          <div className="flex items-center justify-between mb-4">
            {cart.aiProvider && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-navy-050 text-navy-700">
                Provider: {cart.aiProvider.charAt(0).toUpperCase() + cart.aiProvider.slice(1)}
              </span>
            )}
            {itemCount > 0 && !submitResult && (
              <button
                onClick={clearCart}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Results view */}
          {submitResult && (
            <div className="mb-4">
              <div className={`rounded-md p-4 ${submitResult.success ? 'bg-positive-100 border border-stone-200' : 'bg-critical-100 border border-stone-200'}`}>
                <div className="flex items-start gap-2">
                  {submitResult.success ? (
                    <svg className="w-5 h-5 text-positive-700 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-critical-700 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  <div className="flex-1">
                    <h4 className="text-sm font-medium">
                      {submitResult.dryRun ? 'Dry Run Complete' : submitResult.success ? 'Batch Submitted' : 'Submission Failed'}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {submitResult.jobsCreated} jobs {submitResult.dryRun ? 'would be' : ''} created
                      {submitResult.batchId && ` (batch: ${submitResult.batchId.slice(0, 8)}...)`}
                    </p>
                    {Object.entries(submitResult.itemsByReviewType).length > 0 && (
                      <div className="mt-2 text-xs">
                        {Object.entries(submitResult.itemsByReviewType).map(([type, count]) => (
                          <span key={type} className="inline-block mr-2 px-1.5 py-0.5 bg-white/50 rounded">
                            {type.replace(/_/g, ' ')}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                    {submitResult.skipped.length > 0 && (
                      <p className="text-xs text-caution-700 mt-1">
                        {submitResult.skipped.length} items skipped (active jobs exist)
                      </p>
                    )}
                    {submitResult.errors.length > 0 && (
                      <div className="mt-2">
                        {submitResult.errors.map((err, i) => (
                          <p key={i} className="text-xs text-critical-700">{err}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => { clearResult(); clearCart(); }}
                className="mt-2 text-sm text-primary-brand hover:underline"
              >
                Start new batch
              </button>
            </div>
          )}

          {/* Items list */}
          {!submitResult && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {itemCount === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <svg className="w-12 h-12 mx-auto mb-3 text-ink-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <p className="text-sm">Queue is empty</p>
                  <p className="text-xs mt-1">Add issuers from the issuer detail page using "Add to Batch"</p>
                </div>
              ) : (
                Object.entries(groupedItems).map(([reviewType, items]) => (
                  <div key={reviewType}>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                      {items[0].reviewTypeLabel} ({items.length})
                    </h4>
                    <div className="space-y-1">
                      {items.map(item => (
                        <div
                          key={item.localId}
                          className="flex items-center justify-between px-3 py-2 bg-muted/50 rounded-md group"
                        >
                          <span className="text-sm truncate flex-1 mr-2">{item.issuerName}</span>
                          <button
                            onClick={() => removeItem(item.localId)}
                            className="text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                            title="Remove"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Footer */}
          {!submitResult && itemCount > 0 && canCreateBatchReview && (
            <div className="border-t border-border pt-4 mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                <span className="text-xs">50% batch discount applied</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleSubmitClick(true)}
                  disabled={submitting}
                  className="btn-outline flex-1 text-sm"
                >
                  Dry Run
                </button>
                <button
                  onClick={() => handleSubmitClick(false)}
                  disabled={submitting}
                  className="btn-primary flex-1 text-sm"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Batch'
                  )}
                </button>
              </div>
            </div>
          )}

          {showUpgrade && (
            <div className="border-t border-border pt-4 mt-4">
              <div className="rounded-lg border border-stone-200 bg-caution-100 p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-caution-700 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-caution-700">Upgrade to Institutional</h4>
                    <p className="text-xs text-ink-700 mt-1">
                      Batch LLM review is available on the Institutional plan. Submit bulk reviews at a 50% discount with priority processing.
                    </p>
                    <Link
                      to="/profile?tab=subscription"
                      onClick={() => setIsOpen(false)}
                      className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-caution-700 hover:text-caution-700 no-underline"
                    >
                      View plans
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Drawer>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleSubmit}
        title={dryRunMode ? 'Dry Run Batch?' : 'Submit Batch Review?'}
        message={
          dryRunMode
            ? `This will validate ${itemCount} items without creating jobs. No API calls will be made.`
            : `This will submit ${itemCount} items to ${cart.aiProvider || 'Grok'} batch API. Jobs will be processed within 24 hours.`
        }
        confirmLabel={dryRunMode ? 'Run Dry Run' : 'Submit Batch'}
        variant={dryRunMode ? 'default' : 'warning'}
        isLoading={submitting}
      />
    </>
  );
};

export default BatchCartDrawer;
